import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import { t } from 'i18next'
import { Building } from 'models/Building'
import useSWR from 'swr'
import CommonFunction from 'utils/commonFunction'

import CustomModal from 'components/atoms/Modal'

export default function MapLocationModal({
  setOpen
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const isMobileScreen = useMediaQuery({ query: '(max-width: 768px)' })

  const { data: buildingInfo } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })

  const [locationUrl, setLocationUrl] = useState<string>('')
  const [embedLocationUrl, setEmbedLocationUrl] = useState<string>('')

  // Set expanded URL for iframe rendering
  useEffect(() => {
    const handleFetchLocationUrl = async () => {
      if (buildingInfo?.extraInfo?.locationEmbed) {
        setEmbedLocationUrl(buildingInfo.extraInfo.locationEmbed)
        return
      }

      if (buildingInfo?.extraInfo?.locationUrl) {
        const url = buildingInfo.extraInfo.locationUrl
        const s3Domain = process.env.REACT_APP_S3_DOMAIN
        const isS3Link = s3Domain && url.startsWith(s3Domain)

        if (isS3Link) {
          try {
            const fetchedContent =
              await CommonFunction.FetchTourLocationContentFromS3Link(url)
            setLocationUrl(fetchedContent)
          } catch (error) {
            console.error('Failed to fetch location from S3:', error)
            setLocationUrl('')
          }
        } else {
          setLocationUrl(url)
        }
      } else {
        setEmbedLocationUrl('')
        setLocationUrl('')
      }
    }

    handleFetchLocationUrl()
  }, [buildingInfo])

  const iframeSrc = embedLocationUrl
    ? CommonFunction.getEmbedUrl(embedLocationUrl)
    : CommonFunction.getEmbedUrl(locationUrl)

  const handleClose = () => setOpen(false)

  return (
    <CustomModal
      width={isMobileScreen ? '100vw' : '90vw'}
      bodyStyle={{
        height: isMobileScreen ? '100vh' : '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
      rootClassName="custom-annotation-detail-modal"
      maskClosable
      onCancel={handleClose}
      open
      footer={null}
      destroyOnClose
      centered
    >
      <div
        className={`absolute top-2 right-2 cursor-pointer z-50 text-white leading-none select-none ${
          isMobileScreen ? 'text-md' : 'text-2xl'
        }`}
        onClick={handleClose}
      >
        ×
      </div>

      <div className="flex flex-col h-[80vh] md:h-full overflow-hidden p-2 ">
        <div className="flex-1 w-full min-h-0 flex items-center justify-center bg-white py-[4%] px-[2%] md:p-[1%] rounded-[5px]">
          {buildingInfo?.extraInfo && (
            <iframe
              src={iframeSrc || ''}
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Map"
            />
          )}
        </div>
        <div className="mt-4 hidden md:flex justify-center items-center">
          <div
            className="underline text-center text-white cursor-pointer text-lg md:text-2xl opacity-80 hover:opacity-100 transition-opacity"
            onClick={() => {
              window.open(iframeSrc || '', '_blank')
            }}
          >
            {t('building.mapSetting.openMapNewTab')}
          </div>
        </div>
      </div>
    </CustomModal>
  )
}
